import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { GridRowData } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';

const DragDataTable: React.FC<{
  rows: any[];
  columns: any;
  onDragEnd: (draggedId, currPos, tarPos) => void;
  setSelected: (params: GridRowData[]) => void;
  selected: GridRowData[];
  enableSelectAll: boolean;
  page: number;
  pageSize: number;
}> = ({
  rows,
  columns,
  onDragEnd,
  setSelected,
  selected,
  enableSelectAll,
  page,
  pageSize,
}) => {
  const [localItems, setLocalItems] = useState<Array<any>>(rows);
  useEffect(() => {
    let startRow = (page - 1) * pageSize;
    startRow = startRow < 0 ? 0 : startRow;
    const endRow = startRow + pageSize;
    const paginatedRows = rows.slice(startRow, endRow);
    setLocalItems(paginatedRows);
  }, [rows, page, pageSize]);

  const handleDragEnd = async (
    result: DropResult,
    provided?: ResponderProvided
  ) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const draggedItem = rows.find(
      (el) => Number(el.id) === Number(result?.draggableId)
    );
    const currentPosition = draggedItem.order;
    const destinationItem = localItems[result.destination.index];
    const targetPosition = Math.max(
      destinationItem?.order,
      Number(result?.destination?.index) + 1
    );
    onDragEnd(draggedItem.id, currentPosition, targetPosition);
  };

  return (
    <TableContainer>
      <Table>
        <colgroup>
          <col style={{ width: '5%' }} />
          <col style={{ width: '5%' }} />
          <col style={{ width: '35%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((col) =>
              col.field === 'check' ? (
                <TableCell key={col.field} style={{ textAlign: 'center' }}>
                  <Checkbox
                    disabled={!enableSelectAll}
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < rows.length
                    }
                    checked={
                      selected.length > 0 && selected.length === rows.length
                    }
                    onClick={(event) => {
                      selected.length ? setSelected([]) : setSelected(rows);
                    }}
                  />
                </TableCell>
              ) : (
                <TableCell key={col.field} align={col.headerAlign}>
                  {col.headerName}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        {localItems.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(droppableProvided: DroppableProvided) => (
                <TableBody
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {localItems.map((item: any, index: number) => {
                    return (
                      <Draggable
                        key={item.id}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(
                          draggableProvided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => {
                          return (
                            <TableRow
                              data-pup={`rule-row-${index}`}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                                background: snapshot.isDragging
                                  ? 'rgba(245,245,245, 0.75)'
                                  : 'none',
                              }}
                            >
                              {Object.keys(item).map((keyName, keyIndex) =>
                                keyName === 'check' ? (
                                  <TableCell
                                    key={keyName}
                                    className="selectCheckbox"
                                    padding="checkbox"
                                    align="center"
                                  >
                                    <Checkbox
                                      color="primary"
                                      onClick={(event) => {
                                        const updated =
                                          selected.indexOf(item) === -1
                                            ? [...selected, item]
                                            : selected.filter(
                                                (el) => el.id !== item.id
                                              );
                                        setSelected(updated);
                                      }}
                                      className="selectCheckbox"
                                      checked={selected.indexOf(item) !== -1}
                                    />
                                  </TableCell>
                                ) : keyName === 'order' ? (
                                  <TableCell key={keyName} align="center">
                                    <Tooltip title="Drag to reorder">
                                      <div
                                        {...draggableProvided.dragHandleProps}
                                      >
                                        <DragIndicatorIcon />
                                      </div>
                                    </Tooltip>
                                  </TableCell>
                                ) : keyName === 'numbers' ? (
                                  <TableCell key={keyName}>
                                    {columns
                                      .find((col) => col.field === 'numbers')
                                      .renderCell(item)}
                                  </TableCell>
                                ) : keyName === 'did' ? (
                                  <TableCell key={keyName}>
                                    {columns
                                      .find((col) => col.field === 'did')
                                      .renderCell(item)}
                                  </TableCell>
                                ) : keyName === 'active' ? (
                                  <TableCell key={keyName}>
                                    {columns
                                      .find((col) => col.field === 'active')
                                      .renderCell(item)}
                                  </TableCell>
                                ) : keyName === 'id' ? (
                                  <TableCell align="right" key={keyName}>
                                    {columns
                                      .find((col) => col.field === 'id')
                                      .renderCell(item[keyName])}
                                  </TableCell>
                                ) : (
                                  <TableCell key={keyName}>
                                    {item[keyName]}
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="right">No Rows</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default DragDataTable;
